import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi, ErreurApi, urlApi } from './api';

/**
 * Certificats de réalisation des échéances d'apprentissage (TF-412).
 *
 * À partir de la deuxième échéance, chaque facture du contrat se joint d'un
 * certificat. L'API propose son contenu depuis le contrat et les paramètres de
 * facturation (`domain/certificat-realisation.ts`) ; l'écran le laisse
 * corriger, en montre l'aperçu, puis le fait produire. Ce qui empêche de
 * l'établir vient de l'API : l'écran ne le recalcule pas.
 */

export type NatureAction = 'ACTION_FORMATION' | 'BILAN_COMPETENCES' | 'VAE' | 'APPRENTISSAGE';
export type UniteDuree = 'MOIS' | 'HEURE' | 'JOUR';

/** Le contenu d'un certificat, tel qu'il se saisit : jours `AAAA-MM-JJ`, durée « 6.00 ». */
export interface ContenuCertificat {
  natureAction: NatureAction;
  intituleAction: string;
  entreprise: string;
  periodeDebut: string;
  periodeFin: string;
  dureeRealisee: string;
  uniteDuree: UniteDuree;
  lieuSignature: string;
  dateCertificat: string;
  signataireNom: string;
  signataireQualite: string;
}

export interface CertificatDeLEcheance {
  echeance: { id: string; rang: number; dateDebut: string; dateFin: string };
  /** « HOARAU KÉVIN » : repris du dossier. */
  beneficiaire: string;
  certificat: { id: string; contenu: ContenuCertificat; genereLe: string; generePar: string | null } | null;
  proposition: ContenuCertificat;
  /** Une image de cachet est déposée dans les paramètres de facturation. */
  cachet: boolean;
  refus: string | null;
  natures: { valeur: NatureAction; libelle: string }[];
  unites: { valeur: UniteDuree; libelle: string }[];
}

/** Le certificat produit, servi par une route gardée et tracée. */
export function urlFichierCertificat(id: string): string {
  return urlApi(`/certificats/${id}/fichier`);
}

export function useCertificatEcheance(echeanceId: string) {
  return useQuery({
    queryKey: ['certificats', echeanceId],
    queryFn: () => appelApi<CertificatDeLEcheance>(`/certificats/echeances/${echeanceId}`),
    // Ouverte pour saisir : la proposition doit refléter le contrat du moment.
    staleTime: 0,
  });
}

/** Produit ou régénère le certificat : l'échéancier et les factures le reflètent. */
export function useGenererCertificat() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ echeanceId, contenu }: { echeanceId: string; contenu: ContenuCertificat }) =>
      appelApi<CertificatDeLEcheance>(`/certificats/echeances/${echeanceId}`, { methode: 'PUT', corps: contenu }),
    onSuccess: (_resultat, { echeanceId }) => {
      void client.invalidateQueries({ queryKey: ['financements'] });
      void client.invalidateQueries({ queryKey: ['certificats', echeanceId] });
    },
  });
}

/**
 * L'aperçu du contenu saisi, sans rien enregistrer. Il passe par `fetch`
 * plutôt que par un lien : le contenu voyage dans le corps de la requête, et
 * un refus doit s'afficher à l'écran, pas s'ouvrir comme un faux PDF. La
 * fenêtre est ouverte avant l'appel — après, le navigateur la bloquerait.
 */
export async function ouvrirApercuCertificat(echeanceId: string, contenu: ContenuCertificat): Promise<void> {
  const fenetre = window.open('', '_blank');
  try {
    const reponse = await fetch(urlApi(`/certificats/echeances/${echeanceId}/apercu.pdf`), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contenu),
    });
    if (!reponse.ok) {
      const corps = (await reponse.json().catch(() => null)) as
        | { erreur?: { code?: string; message?: string; details?: unknown } }
        | null;
      throw new ErreurApi(
        reponse.status,
        corps?.erreur?.code ?? 'ERREUR',
        corps?.erreur?.message ?? 'L’aperçu n’a pas pu être produit.',
        corps?.erreur?.details,
      );
    }
    const url = URL.createObjectURL(await reponse.blob());
    if (fenetre) {
      fenetre.location.href = url;
    } else {
      window.open(url, '_blank');
    }
    // La fenêtre a chargé le document : l'adresse peut être libérée ensuite.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (erreur) {
    fenetre?.close();
    throw erreur;
  }
}
